/**
 * WatchNoteProcessedSheet
 *
 * Celebratory bottom sheet shown on the iPhone after a voice note recorded
 * on the Apple Watch finishes uploading + AI-processing on the backend. The
 * watch has nowhere near enough room to render the summarized content so it
 * just shows "Sent to phone" — the user's reward for the interaction lives
 * here.
 *
 * Data contract: `request` comes from `WatchProcessedNoteRequest` in
 * `hooks/useWatchSync.ts`. The phone calls `onNoteProcessed(request)` from
 * inside the `voice_note_inline` / `file_received` handlers once the
 * `createNote` dispatch resolves. `request.summary` is the
 * backend-generated AI summary (the `response` column on the note row);
 * falls back to `body` (raw transcript, if the watch provided one) and then
 * to a "Saved" state if neither is present.
 *
 * Actions:
 *   - "Open in Notes" navigates to the project's Notes screen (Plans stack
 *     → ProjectNotes). Dismisses the sheet.
 *   - "Done" just dismisses.
 */

import React, { useEffect, useMemo, useRef } from 'react';
import {
  Modal,
  View,
  Text,
  TouchableOpacity,
  ScrollView,
  StyleSheet,
  Pressable,
  Animated,
  Easing,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useNavigation } from '@react-navigation/native';
import * as Haptics from 'expo-haptics';
import { useTheme } from '../hooks/useTheme';
import { useSheetLayout } from '../utilities/sheetLayout';
import Icon from './Icon';
import type { AppTheme } from '../theme';
import type { WatchProcessedNoteRequest } from '../hooks/useWatchSync';

interface Props {
  request: WatchProcessedNoteRequest | null;
  onClose: () => void;
}

export default function WatchNoteProcessedSheet({ request, onClose }: Props) {
  const theme = useTheme();
  const insets = useSafeAreaInsets();
  const navigation = useNavigation<any>();
  const sheet = useSheetLayout({ desktopMaxWidth: 480 });
  const styles = useMemo(() => makeStyles(theme, insets), [theme, insets]);

  // Subtle pulse on the sparkle badge — signals "just processed" without
  // being loud about it. Loops while the sheet is open and stops on close.
  const pulse = useRef(new Animated.Value(0)).current;
  const visible = !!request;

  useEffect(() => {
    if (!visible) return;
    // Celebratory tap when the sheet appears. Soft enough that a busy user
    // isn't startled but definite enough to register as a completion.
    Haptics.notificationAsync(Haptics.NotificationFeedbackType.Success).catch(() => {});

    const loop = Animated.loop(
      Animated.sequence([
        Animated.timing(pulse, {
          toValue: 1,
          duration: 1200,
          easing: Easing.inOut(Easing.quad),
          useNativeDriver: true,
        }),
        Animated.timing(pulse, {
          toValue: 0,
          duration: 1200,
          easing: Easing.inOut(Easing.quad),
          useNativeDriver: true,
        }),
      ]),
    );
    loop.start();
    return () => {
      loop.stop();
      pulse.setValue(0);
    };
  }, [visible, pulse]);

  if (!request) return null;

  const haveSummary = !!(request.summary && request.summary.trim().length > 0);
  // `body` is either a real transcript (good) or the fallback title the
  // watch-sync handler synthesized (e.g. "Watch Note — 3:45 PM"). Hide the
  // body card when it's the fallback and we have nothing else interesting,
  // so we don't show "Watch Note — 3:45 PM" twice.
  const bodyLooksLikeFallback = !!request.body && /^Watch Note — /.test(request.body);
  const haveBody = !!(request.body && request.body.trim().length > 0 && !bodyLooksLikeFallback);
  const processing = !haveSummary && !haveBody;

  const subtitleParts: string[] = [];
  if (request.projectName) subtitleParts.push(request.projectName);
  if (request.createdAt) {
    const d = new Date(request.createdAt);
    if (!Number.isNaN(d.getTime())) {
      subtitleParts.push(
        d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' }),
      );
    }
  }

  const titleLooksLikeFallback = /^Watch Note — /.test(request.title);
  const hasMeaningfulTitle = !titleLooksLikeFallback && request.title.trim().length > 0;

  const handleOpenInNotes = () => {
    // Plans is the top-level tab; ProjectNotes is the stack screen with the
    // list that contains the note they just made.
    try {
      navigation.navigate('Plans', {
        screen: 'ProjectNotes',
        params: {
          projectId: request.projectId,
        },
      });
    } catch {
      // Navigation can fail if the Plans stack isn't mounted yet (e.g.
      // deep-link edge cases). Close the sheet regardless so the user
      // isn't stuck staring at it.
    }
    onClose();
  };

  const scale = pulse.interpolate({ inputRange: [0, 1], outputRange: [1, 1.08] });
  const glow = pulse.interpolate({ inputRange: [0, 1], outputRange: [0.55, 0.95] });

  return (
    <Modal
      visible={visible}
      animationType={sheet.animationType}
      transparent
      onRequestClose={onClose}
    >
      <View style={[styles.overlay, sheet.overlayStyle]}>
        <Pressable style={StyleSheet.absoluteFill} onPress={onClose} />
        <View style={[styles.sheet, sheet.sheetStyle]}>
          {sheet.showHandle && <View style={styles.handle} />}

          <ScrollView
            showsVerticalScrollIndicator={false}
            contentContainerStyle={[styles.content, sheet.desktopContentStyle]}
          >
            {/* Sparkle badge + watch glyph */}
            <View style={styles.badgeRow}>
              <Animated.View
                style={[
                  styles.badgeHalo,
                  { backgroundColor: `${theme.colors.primary}22`, opacity: glow, transform: [{ scale }] },
                ]}
              />
              <View style={[styles.badge, { backgroundColor: theme.colors.primary }]}>
                <Icon icon={['fad', 'wand-magic-sparkles']} size={22} color={theme.colors.primaryText} />
              </View>
              <View style={[styles.badgeWatch, { borderColor: theme.colors.border, backgroundColor: theme.colors.surface }]}>
                <Icon icon={['fad', 'watch']} size={12} color={theme.colors.primary} />
              </View>
            </View>

            <Text style={styles.heading}>
              {processing ? 'Note received' : 'Note processed'}
            </Text>
            {subtitleParts.length > 0 && (
              <Text style={styles.subtitle}>
                {subtitleParts.join(' · ')}
              </Text>
            )}

            {hasMeaningfulTitle && (
              <View style={[styles.card, styles.titleCard]}>
                <Text style={styles.cardTitle}>{request.title}</Text>
              </View>
            )}

            {haveSummary && (
              <View style={styles.card}>
                <View style={styles.cardHeader}>
                  <View style={[styles.cardIconWrap, { backgroundColor: `${theme.colors.primary}18` }]}>
                    <Icon icon={['fad', 'sparkles']} size={14} color={theme.colors.primary} />
                  </View>
                  <Text style={styles.cardHeaderText}>Summary</Text>
                </View>
                <Text style={styles.cardBody}>{request.summary}</Text>
              </View>
            )}

            {haveBody && (
              <View style={styles.card}>
                <View style={styles.cardHeader}>
                  <View style={[styles.cardIconWrap, { backgroundColor: `${theme.colors.muted}18` }]}>
                    <Icon icon={['fad', 'waveform-lines']} size={14} color={theme.colors.muted} />
                  </View>
                  <Text style={styles.cardHeaderText}>Transcript</Text>
                </View>
                <Text style={styles.cardBody}>{request.body}</Text>
              </View>
            )}

            {processing && (
              <View style={[styles.card, styles.processingCard]}>
                <Text style={styles.processingText}>
                  Your audio is saved and waiting to be transcribed. It will
                  appear in this plan&apos;s Notes as soon as it&apos;s ready.
                </Text>
              </View>
            )}
          </ScrollView>

          <View style={styles.ctaWrap}>
            <TouchableOpacity
              style={[styles.btn, styles.secondaryBtn]}
              onPress={onClose}
              activeOpacity={0.7}
            >
              <Text style={styles.secondaryBtnText}>Done</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.btn, styles.primaryBtn, { backgroundColor: theme.colors.primary }]}
              onPress={handleOpenInNotes}
              activeOpacity={0.85}
            >
              <Text style={[styles.primaryBtnText, { color: theme.colors.primaryText }]}>
                Open in Notes
              </Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>
  );
}

const makeStyles = (theme: AppTheme, insets: { bottom: number }) =>
  StyleSheet.create({
    overlay: {
      flex: 1,
      justifyContent: 'flex-end',
      backgroundColor: 'rgba(0,0,0,0.45)',
    },
    sheet: {
      backgroundColor: theme.colors.card,
      borderTopLeftRadius: 22,
      borderTopRightRadius: 22,
      maxHeight: '85%',
    },
    handle: {
      width: 36, height: 4, borderRadius: 2,
      backgroundColor: theme.colors.border,
      alignSelf: 'center',
      marginTop: 10, marginBottom: 4,
    },
    content: {
      paddingHorizontal: theme.spacing.md,
      paddingTop: theme.spacing.sm,
      paddingBottom: theme.spacing.md,
      alignItems: 'stretch',
    },
    badgeRow: {
      alignSelf: 'center',
      width: 64,
      height: 64,
      marginTop: 6,
      marginBottom: theme.spacing.sm,
      alignItems: 'center',
      justifyContent: 'center',
    },
    badgeHalo: {
      position: 'absolute',
      width: 64,
      height: 64,
      borderRadius: 32,
    },
    badge: {
      width: 52,
      height: 52,
      borderRadius: 26,
      alignItems: 'center',
      justifyContent: 'center',
      shadowColor: theme.colors.primary,
      shadowOpacity: 0.45,
      shadowRadius: 12,
      shadowOffset: { width: 0, height: 4 },
      elevation: 6,
    },
    badgeWatch: {
      position: 'absolute',
      bottom: -2,
      right: -2,
      width: 22,
      height: 22,
      borderRadius: 11,
      alignItems: 'center',
      justifyContent: 'center',
      borderWidth: 2,
    },
    heading: {
      fontSize: theme.font.size.lg,
      fontWeight: theme.font.weight.bold,
      color: theme.colors.text,
      textAlign: 'center',
      marginBottom: 2,
    },
    subtitle: {
      fontSize: theme.font.size.sm,
      color: theme.colors.muted,
      textAlign: 'center',
      marginBottom: theme.spacing.md,
    },
    card: {
      backgroundColor: theme.colors.surface,
      borderRadius: theme.radius.md,
      borderWidth: StyleSheet.hairlineWidth,
      borderColor: theme.colors.border,
      paddingVertical: 12,
      paddingHorizontal: 14,
      marginBottom: theme.spacing.sm,
    },
    titleCard: {
      paddingVertical: 10,
    },
    cardTitle: {
      fontSize: theme.font.size.md,
      fontWeight: theme.font.weight.semibold,
      color: theme.colors.text,
    },
    cardHeader: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 8,
      marginBottom: 6,
    },
    cardIconWrap: {
      width: 24,
      height: 24,
      borderRadius: 12,
      alignItems: 'center',
      justifyContent: 'center',
    },
    cardHeaderText: {
      fontSize: theme.font.size.sm,
      fontWeight: theme.font.weight.semibold,
      color: theme.colors.text,
    },
    cardBody: {
      fontSize: theme.font.size.sm,
      color: theme.colors.text,
      lineHeight: 20,
    },
    processingCard: {
      backgroundColor: theme.colors.surface,
    },
    processingText: {
      fontSize: theme.font.size.sm,
      color: theme.colors.muted,
      lineHeight: 20,
    },
    ctaWrap: {
      flexDirection: 'row',
      gap: 10,
      paddingHorizontal: theme.spacing.md,
      paddingTop: theme.spacing.sm,
      paddingBottom: insets.bottom + 12,
      borderTopWidth: StyleSheet.hairlineWidth,
      borderTopColor: theme.colors.border,
      backgroundColor: theme.colors.card,
    },
    btn: {
      flex: 1,
      borderRadius: theme.radius.md,
      paddingVertical: 13,
      alignItems: 'center',
      justifyContent: 'center',
    },
    primaryBtn: {
      shadowColor: theme.colors.primary,
      shadowOpacity: 0.22,
      shadowRadius: 8,
      shadowOffset: { width: 0, height: 2 },
      elevation: 3,
    },
    secondaryBtn: {
      backgroundColor: theme.colors.surface,
      borderWidth: StyleSheet.hairlineWidth,
      borderColor: theme.colors.border,
    },
    primaryBtnText: {
      fontSize: theme.font.size.md,
      fontWeight: theme.font.weight.semibold,
    },
    secondaryBtnText: {
      fontSize: theme.font.size.md,
      fontWeight: theme.font.weight.semibold,
      color: theme.colors.text,
    },
  });
